import { graphql, useStaticQuery } from 'gatsby';
import React from 'react';
import Helmet from 'react-helmet';

export interface SEOProps {
  title?: string;
  metaTitle?: string;
  description?: string;
  metaDescription?: string;
  structuredData?: string;
  organisationSchema?:string;
  slug?: string;
  featuredImageUrl?: string;
  websiteType?: string;
  type?: string;
  topic?: string;
  tags?: string[];
}

const htmlAttributes = {
  lang: 'en',
};

function SEO({
  title,
  metaTitle,
  description,
  metaDescription,
  structuredData,
  organisationSchema,
  slug,
  featuredImageUrl,
  websiteType,
  type,
  topic,
  tags,
}: SEOProps) {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {       
            title
            description
          }
        }
      }
    `,
  );

  const mDescription = metaDescription ? metaDescription : description;
  const mTitle = metaTitle ? metaTitle : title;
  const url = `https://www.ep.com/${slug || ''}`;
  const structuredDataString = structuredData ? JSON.stringify(structuredData) : null;
  
  return (
    <Helmet htmlAttributes={htmlAttributes}>
      <title>
        {mTitle} - {site.siteMetadata.title}
      </title>
      <meta name="description" content={mDescription} />
      <meta
        name="robots"
        content="max-snippet:-1, max-image-preview:large, max-video-preview:-1"
      />
      <meta name="website-category" content={websiteType} />
      {type && <meta name="webpage-type" content={type} />}
      {topic && <meta name="webpage-topic" content={topic} />}
      {tags && <meta name="webpage-tags" content={tags.toString()} />}
      <link rel="canonical" href={url} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={mTitle} />
      <meta property="og:description" content={mDescription} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content="Entertainment Partners" />
      {featuredImageUrl && (
        <meta property="og:image" content={featuredImageUrl} />
      )}
      {featuredImageUrl && (
        <meta property="og:image:secure_url" content={featuredImageUrl} />
      )}
      {featuredImageUrl && <meta property="og:image:width" content="718" />}
      {featuredImageUrl && <meta property="og:image:height" content="480" />}
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:description" content={mDescription} />
      <meta name="twitter:title" content={mTitle} />
      {featuredImageUrl && (
        <meta name="twitter:image" content={featuredImageUrl} />
      )}
      {structuredData && 
        <script id="structured-data" type="application/ld+json">
          {structuredDataString}
        </script>
      }
      {organisationSchema && 
        <script id="structured-data" type="application/ld+json">
          {organisationSchema}
        </script>
      }
    </Helmet>
  );
}

export default React.memo(SEO);
